<template>
  <div class="custom-toast">
    <transition name="toast-transition">
      <div
        v-if="localSnackbar"
        class="toast-shell"
        id="custom-snackbar"
        role="status"
        aria-live="polite"
      >
        <div class="toast-content" :class="toastToneClass">
          <div class="toast-glow"></div>
          <div class="toast-icon">
            <v-icon size="18" class="toast-icon__glyph">{{ getIcon }}</v-icon>
          </div>

          <div class="toast-message">{{ normalizedMessage }}</div>

          <div class="toast-close">
            <v-btn
              id="close-snackbar-btn"
              class="toast-close__btn"
              text
              x-small
              icon
              @click="barClose"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>

          <div
            v-if="showProgress"
            :key="progressKey"
            class="toast-progress-container"
          >
            <div class="toast-progress" :style="progressStyle"></div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { normalizeSnackbarMessage } from "@/utilities/messageFormatter";

export default {
  name: "CustomToastV2",
  props: {
    snackbar: {
      type: Boolean,
      default: false,
    },
    message: {
      type: String,
      default: "",
    },
    color: {
      type: String,
      default: "success",
      validator: (value) => ["success", "pending", "error", "warning"].includes(value),
    },
    timeout: {
      type: Number,
      default: 3000,
    },
  },
  data() {
    return {
      localSnackbar: Boolean(this.snackbar),
      hideTimer: null,
      progressKey: 0,
    };
  },
  computed: {
    // Tone-specific wrapper class used for styling
    toastToneClass() {
      const toneMap = {
        success: "toast-success",
        pending: "toast-pending",
        error: "toast-error",
        warning: "toast-pending", // Map warning to pending styling
      };
      return toneMap[this.color] || "toast-error";
    },
    // Computed property for icon
    getIcon() {
      const iconMap = {
        success: "mdi-check-circle",
        pending: "mdi-alert",
        error: "mdi-close-circle",
        warning: "mdi-alert", // Map warning to pending icon
      };
      return iconMap[this.color] || "mdi-close-circle";
    },
    // Normalized message in sentence case
    normalizedMessage() {
      return normalizeSnackbarMessage(this.message);
    },
    showProgress() {
      return this.timeout > 0;
    },
    progressStyle() {
      return {
        "--toast-timeout": `${this.timeout}ms`,
      };
    },
  },
  methods: {
    openToast() {
      this.progressKey += 1;
      this.localSnackbar = true;
      this.queueHide();
    },
    dismissToast() {
      this.clearHideTimer();
      this.localSnackbar = false;
      this.progressKey += 1;
    },
    queueHide() {
      this.clearHideTimer();
      if (this.timeout > 0) {
        this.hideTimer = setTimeout(() => {
          this.barClose();
        }, this.timeout);
      }
    },
    clearHideTimer() {
      if (this.hideTimer) {
        clearTimeout(this.hideTimer);
        this.hideTimer = null;
      }
    },
    barClose() {
      this.dismissToast();
      this.$store.commit("setToastData", {
        snackbar: false,
        message: "",
        color: "success",
      });
    },
  },
  watch: {
    snackbar: {
      immediate: true,
      handler(value) {
        if (value) {
          this.openToast();
        } else {
          this.dismissToast();
        }
      },
    },
    message() {
      if (this.snackbar) {
        this.progressKey += 1;
        this.queueHide();
      }
    },
  },
  beforeDestroy() {
    this.clearHideTimer();
  },
};
</script>

<style>
.custom-toast {
  position: fixed;
  top: 24px;
  right: 24px;
  z-index: 250;
  width: auto;
  pointer-events: none;
}

.toast-shell {
  display: flex;
  justify-content: flex-end;
  width: 100%;
  pointer-events: none;
}

.toast-content {
  display: flex;
  gap: 10px;
  align-items: center;
  min-width: 300px;
  max-width: 450px;
  width: max-content;
  padding: 12px 16px 16px 14px;
  border-radius: 12px;
  background: var(--toast-bg);
  color: var(--toast-text) !important;
  border: 1px solid var(--toast-border);
  box-shadow: 0 12px 32px -12px rgba(15, 23, 42, 0.24);
  position: relative;
  overflow: hidden;
  pointer-events: auto;
  animation: toast-pop 360ms cubic-bezier(0.21, 0.8, 0.43, 0.99);
  backdrop-filter: blur(18px) saturate(150%);
}

.toast-content::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: radial-gradient(
    circle at top right,
    rgba(255, 255, 255, 0.38),
    transparent 65%
  );
  pointer-events: none;
}

.toast-content::after {
  content: "";
  position: absolute;
  inset: 1px;
  border-radius: 11px;
  border: 1px solid var(--toast-inner-border);
  opacity: 0.6;
  pointer-events: none;
}

.toast-glow {
  position: absolute;
  inset: 0;
  background: radial-gradient(
    120% 120% at -20% -20%,
    var(--toast-glow),
    transparent 55%
  );
  opacity: 0.6;
  pointer-events: none;
  z-index: 0;
}

.toast-icon {
  z-index: 1;
  flex: 0 0 32px;
  height: 32px;
  width: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: var(--toast-accent-surface);
  border: 1px solid var(--toast-icon-border);
  box-shadow: 0 8px 20px -8px var(--toast-accent-shadow);
}

.toast-icon__glyph {
  color: var(--toast-accent) !important;
}

.toast-message {
  z-index: 1;
  color: inherit !important;
  font-size: 0.875rem;
  line-height: 1.4;
  font-weight: 500;
  letter-spacing: 0.01em;
}

.toast-close {
  z-index: 1;
  margin-left: auto;
  margin-right: -4px;
}

.toast-close__btn {
  min-width: 28px !important;
  min-height: 28px !important;
  padding: 4px !important;
  border-radius: 8px !important;
  color: var(--toast-accent) !important;
  transition: background-color 0.2s ease, color 0.2s ease;
  background-color: transparent !important;
  border: 1px solid transparent !important;
}

.toast-close__btn:hover {
  background-color: var(--toast-accent-hover) !important;
  color: var(--toast-text) !important;
  border-color: var(--toast-accent-hover-border) !important;
}

.toast-progress-container {
  position: absolute;
  inset: auto 14px 8px 14px;
  height: 3px;
  border-radius: 999px;
  background: var(--toast-progress-track);
  overflow: hidden;
  z-index: 1;
}

.toast-progress {
  height: 100%;
  width: 100%;
  background: var(--toast-progress);
  animation: toast-progress var(--toast-timeout, 3000ms) linear forwards;
  transform-origin: left center;
}

.toast-success {
  --toast-bg: linear-gradient(135deg, #f2fbf5 0%, #e0f4e6 100%);
  --toast-text: #104329;
  --toast-accent: #21a366;
  --toast-accent-hover: rgba(33, 163, 102, 0.16);
  --toast-accent-hover-border: rgba(33, 163, 102, 0.28);
  --toast-accent-surface: rgba(33, 163, 102, 0.18);
  --toast-icon-border: rgba(33, 163, 102, 0.35);
  --toast-accent-shadow: rgba(33, 163, 102, 0.28);
  --toast-border: rgba(33, 163, 102, 0.22);
  --toast-inner-border: rgba(255, 255, 255, 0.6);
  --toast-glow: rgba(59, 184, 121, 0.35);
  --toast-progress-track: rgba(33, 163, 102, 0.12);
  --toast-progress: linear-gradient(
    90deg,
    rgba(33, 163, 102, 0.8),
    rgba(33, 163, 102, 0.35)
  );
}

.toast-error {
  --toast-bg: linear-gradient(135deg, #fff0f0 0%, #ffe0de 100%);
  --toast-text: #611621;
  --toast-accent: #d13f52;
  --toast-accent-hover: rgba(209, 63, 82, 0.18);
  --toast-accent-hover-border: rgba(209, 63, 82, 0.3);
  --toast-accent-surface: rgba(209, 63, 82, 0.18);
  --toast-icon-border: rgba(209, 63, 82, 0.35);
  --toast-accent-shadow: rgba(209, 63, 82, 0.28);
  --toast-border: rgba(209, 63, 82, 0.22);
  --toast-inner-border: rgba(255, 255, 255, 0.55);
  --toast-glow: rgba(209, 63, 82, 0.32);
  --toast-progress-track: rgba(209, 63, 82, 0.12);
  --toast-progress: linear-gradient(
    90deg,
    rgba(209, 63, 82, 0.8),
    rgba(209, 63, 82, 0.35)
  );
}

.toast-pending {
  --toast-bg: linear-gradient(135deg, #fff9eb 0%, #fff3d3 100%);
  --toast-text: #5d4211;
  --toast-accent: #d4891f;
  --toast-accent-hover: rgba(212, 137, 31, 0.16);
  --toast-accent-hover-border: rgba(212, 137, 31, 0.3);
  --toast-accent-surface: rgba(212, 137, 31, 0.22);
  --toast-icon-border: rgba(212, 137, 31, 0.32);
  --toast-accent-shadow: rgba(212, 137, 31, 0.28);
  --toast-border: rgba(212, 137, 31, 0.2);
  --toast-inner-border: rgba(255, 255, 255, 0.56);
  --toast-glow: rgba(212, 137, 31, 0.28);
  --toast-progress-track: rgba(212, 137, 31, 0.12);
  --toast-progress: linear-gradient(
    90deg,
    rgba(212, 137, 31, 0.8),
    rgba(212, 137, 31, 0.35)
  );
}

.toast-transition-enter-active,
.toast-transition-leave-active {
  transition: opacity 0.28s cubic-bezier(0.2, 0.8, 0.4, 1),
    transform 0.28s cubic-bezier(0.2, 0.8, 0.4, 1);
}

.toast-transition-enter,
.toast-transition-leave-to {
  opacity: 0;
  transform: translateY(-16px) scale(0.94);
}

.toast-transition-leave-active .toast-content {
  filter: blur(1px);
}

@keyframes toast-pop {
  0% {
    opacity: 0;
    transform: translateY(-10px) scale(0.9);
  }
  60% {
    opacity: 1;
    transform: translateY(4px) scale(1.03);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes toast-progress {
  0% {
    transform: scaleX(1);
    opacity: 1;
  }
  100% {
    transform: scaleX(0);
    opacity: 0.3;
  }
}

@media (max-width: 480px) {
  .custom-toast {
    top: 16px;
    right: 16px;
    left: 16px;
  }

  .toast-shell {
    justify-content: center;
  }

  .toast-content {
    min-width: 0;
    width: 100%;
    max-width: 280px;
    padding: 10px 14px 14px 12px;
    gap: 8px;
  }

  .toast-message {
    font-size: 0.8125rem;
  }

  .toast-progress-container {
    inset: auto 12px 6px 12px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .toast-content {
    animation: none;
  }

  .toast-transition-enter-active,
  .toast-transition-leave-active {
    transition: none;
  }

  .toast-progress {
    animation: none;
  }
}
</style>
